import React, { useState, useMemo } from 'react';
import { useContent } from '../../context/ContentContext';
import { SpecialForYouItem, MenuItem } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { formatRupiah } from '../../utils/formatters';
import { NavTabId } from '../common/BottomNav';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  UserRound,
  Clock,
  Coffee,
  Croissant,
  Leaf,
  Truck,
  Coins,
  CalendarX2,
  Lock,
  Ticket,
} from 'lucide-react';

const INDONESIAN_MONTHS: Record<string, number> = {
  jan: 0,
  januari: 0,
  feb: 1,
  februari: 1,
  mar: 2,
  maret: 2,
  apr: 3,
  april: 3,
  mei: 4,
  may: 4,
  jun: 5,
  juni: 5,
  jul: 6,
  juli: 6,
  agu: 7,
  ags: 7,
  agustus: 7,
  aug: 7,
  sep: 8,
  sept: 8,
  september: 8,
  okt: 9,
  oct: 9,
  oktober: 9,
  nov: 10,
  november: 10,
  des: 11,
  dec: 11,
  desember: 11,
};

/**
 * Parses a CMS date string (ISO YYYY-MM-DD or Indonesian date like "31 Des 2026")
 * into an end-of-day or start-of-day timestamp. Returns null if unparseable.
 */
export function parseCmsPromoDate(rawDate?: string, isEndOfDay = true): number | null {
  if (!rawDate || typeof rawDate !== 'string') return null;
  const trimmed = rawDate.trim();
  if (!trimmed) return null;

  // 1. ISO format YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const timePart = isEndOfDay ? 'T23:59:59' : 'T00:00:00';
    const ms = new Date(`${trimmed}${timePart}`).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  // 2. Indonesian / Day-Month-Year format e.g. "31 Okt 2024" or "15 Des 2026"
  const dmyMatch = trimmed.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const monthKey = dmyMatch[2].toLowerCase();
    const year = parseInt(dmyMatch[3], 10);
    const monthIdx = INDONESIAN_MONTHS[monthKey];
    if (monthIdx !== undefined && day >= 1 && day <= 31 && year >= 2000) {
      const d = isEndOfDay
        ? new Date(year, monthIdx, day, 23, 59, 59, 999)
        : new Date(year, monthIdx, day, 0, 0, 0, 0);
      return d.getTime();
    }
  }

  return null;
}

/**
 * Calculates whether a CMS SpecialForYouItem is currently active based on
 * its `is_active` flag and optional `start_date` / `end_date` fields.
 */
export function isSpecialPromoActive(item?: SpecialForYouItem | null): boolean {
  if (!item) return false;
  if (item.is_active === false) return false;

  const now = Date.now();
  const startMs = parseCmsPromoDate(item.start_date, false);
  if (startMs !== null && now < startMs) {
    return false;
  }

  const endMs = parseCmsPromoDate(item.end_date, true);
  if (endMs !== null && now > endMs) {
    return false;
  }

  return true;
}

type PromoFilterCategory = 'all' | 'diskon' | 'ongkir' | 'cashback' | 'member';

interface CouponTheme {
  gradientClass: string;
  glowClass: string;
  badgeClass: string;
  buttonTextClass: string;
  iconRotateClass: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const COUPON_THEMES: CouponTheme[] = [
  {
    gradientClass: 'bg-linear-to-br from-[#0040A1] via-[#0056D2] to-[#0284C7]',
    glowClass: ' -right-8 -top-8 w-32 h-32 bg-white/10 blur-xl',
    badgeClass: 'bg-[#6ffbbe] text-[#002113]',
    buttonTextClass: 'text-[#0040A1]',
    iconRotateClass: 'rotate-6',
    Icon: Coffee,
  },
  {
    gradientClass: 'bg-linear-to-br from-[#78350F] via-[#92400E] to-[#B45309]',
    glowClass: ' -right-6 -bottom-6 w-28 h-28 bg-amber-300/15 blur-lg',
    badgeClass: 'bg-white/20 backdrop-blur-xs text-white',
    buttonTextClass: 'text-amber-900',
    iconRotateClass: '-rotate-6',
    Icon: Croissant,
  },
  {
    gradientClass: 'bg-linear-to-br from-[#065F46] via-[#059669] to-[#10B981]',
    glowClass: ' -right-6 top-0 w-28 h-28 bg-emerald-200/20 blur-lg',
    badgeClass: 'bg-white/20 backdrop-blur-xs text-white',
    buttonTextClass: 'text-emerald-800',
    iconRotateClass: 'rotate-0',
    Icon: Leaf,
  },
  {
    gradientClass: 'bg-linear-to-br from-[#1E1B4B] via-[#312E81] to-[#4338CA]',
    glowClass: ' right-0 -bottom-6 w-28 h-28 bg-indigo-300/15 blur-lg',
    badgeClass: 'bg-white/20 backdrop-blur-xs text-white',
    buttonTextClass: 'text-indigo-950',
    iconRotateClass: 'rotate-0',
    Icon: Truck,
  },
  {
    gradientClass: 'bg-linear-to-br from-[#C2410C] via-[#EA580C] to-[#F97316]',
    glowClass: ' -right-4 -top-4 w-28 h-28 bg-yellow-300/20 blur-lg',
    badgeClass: 'bg-white/20 backdrop-blur-xs text-white',
    buttonTextClass: 'text-orange-950',
    iconRotateClass: 'rotate-0',
    Icon: Coins,
  },
];

function deriveRightPanelBenefit(
  item: SpecialForYouItem,
  linkedProduct?: MenuItem
): { primary: string; secondary: string } {
  const combined = `${item.badge_text || ''} ${item.discount_info || ''} ${item.title || ''}`;

  // 1. Check for percentage e.g. "25%", "35%", "50%"
  const percentMatch = combined.match(/(\d{1,2})\s*%/);
  if (percentMatch) {
    const pct = `${percentMatch[1]}%`;
    if (/cashback|koin|poin/i.test(combined)) {
      return { primary: pct, secondary: 'CASHBACK' };
    }
    return { primary: pct, secondary: 'POTONGAN' };
  }

  // 2. Check for Free / Gratis / Buy 2 Get 1
  if (/gratis\s+ongkir|free\s+ongkir|free\s+delivery/i.test(combined)) {
    return { primary: 'Rp 0', secondary: 'ONGKOS KIRIM' };
  }
  if (/delivery|antar/i.test(combined)) {
    if (item.price > 0) {
      const rb = Math.round(item.price / 1000);
      return { primary: `Rp ${rb}RB`, secondary: 'DELIVERY' };
    }
    return { primary: 'HEMAT', secondary: 'DELIVERY' };
  }
  if (/gratis|free|buy\s*\d+\s*get/i.test(combined)) {
    return { primary: 'GRATIS', secondary: 'SPESIAL' };
  }

  // 3. Check price from CMS or linked product
  const effectivePrice = item.price > 0 ? item.price : linkedProduct?.price || 0;
  if (effectivePrice > 0) {
    if (effectivePrice >= 1000 && effectivePrice % 1000 === 0) {
      return {
        primary: `Rp ${effectivePrice / 1000}RB`,
        secondary: 'HARGA PROMO',
      };
    }
    return {
      primary: formatRupiah(effectivePrice),
      secondary: 'SPESIAL',
    };
  }

  return { primary: 'PROMO', secondary: 'SPESIAL' };
}

function deriveCouponContent(
  item: SpecialForYouItem,
  linkedProduct?: MenuItem,
  index = 0
): {
  badgeLabel: string;
  headline: string;
  subtitle: string;
  description: string;
  periodLabel: string;
} {
  const fallbackBadges = ['POPULER', 'SPESIAL MENU', 'HEMAT HARIAN', 'DELIVERY KILAT', 'MEMBER EKSKLUSIF'];
  const badgeLabel =
    (item.badge_text && item.badge_text.trim()) ||
    (linkedProduct?.badge && linkedProduct.badge.trim()) ||
    fallbackBadges[index % fallbackBadges.length];

  const rawDiscount = (item.discount_info || '').trim();
  const rawTitle = (item.title || linkedProduct?.name || 'Promo Spesial LetonKu').trim();

  // If discount_info is a short punchy label (<= 20 chars), use it as headline and title as subtitle
  let headline = rawTitle.toUpperCase();
  let subtitle = rawDiscount || (item.price > 0 ? `Harga Spesial ${formatRupiah(item.price)}` : 'Penawaran Spesial LetonKu');

  if (rawDiscount && rawDiscount.length <= 20) {
    headline = rawDiscount.toUpperCase();
    subtitle = rawTitle;
  } else if (item.price > 0 && !rawDiscount) {
    headline = rawTitle;
    subtitle = `Spesial ${formatRupiah(item.price)} • ${linkedProduct?.name || 'Menu Pilihan LetonKu'}`;
  }

  const description =
    (item.description && item.description.trim()) ||
    (linkedProduct?.description && linkedProduct.description.trim()) ||
    'Nikmati sajian kopi & menu spesial pilihan LetonKu.';

  const hasStart = Boolean(item.start_date && item.start_date.trim());
  const hasEnd = Boolean(item.end_date && item.end_date.trim());
  let periodLabel = 'Setiap Hari';
  if (hasStart && hasEnd) {
    periodLabel = `${item.start_date!.trim()} - ${item.end_date!.trim()}`;
  } else if (hasEnd) {
    periodLabel = item.end_date!.trim();
  } else if (hasStart) {
    periodLabel = `Mulai ${item.start_date!.trim()}`;
  }

  return {
    badgeLabel,
    headline,
    subtitle,
    description,
    periodLabel,
  };
}

interface PromoCatalogPageProps {
  onBack: () => void;
  onSelectPromo: (promoId: string) => void;
  onNavigateTab: (tab: NavTabId) => void;
}

export const PromoCatalogPage: React.FC<PromoCatalogPageProps> = ({
  onBack,
  onSelectPromo,
  onNavigateTab,
}) => {
  const { data, showToast } = useContent();
  const [selectedCategory, setSelectedCategory] = useState<PromoFilterCategory>('all');
  const [voucherInput, setVoucherInput] = useState<string>('');
  const [brokenBannerIds, setBrokenBannerIds] = useState<Record<string, boolean>>({});

  const allSortedPromos = useMemo(() => {
    const rawList = Array.isArray(data?.specialForYou) ? data.specialForYou : [];
    return [...rawList].sort((a, b) => {
      const aActive = isSpecialPromoActive(a) ? 0 : 1;
      const bActive = isSpecialPromoActive(b) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });
  }, [data?.specialForYou]);

  const activePromosCount = useMemo(() => {
    return allSortedPromos.filter((item) => isSpecialPromoActive(item)).length;
  }, [allSortedPromos]);

  const filteredPromos = useMemo(() => {
    if (selectedCategory === 'all') return allSortedPromos;

    const keywordMatches = allSortedPromos.filter((item) => {
      const text = `${item.title || ''} ${item.description || ''} ${item.discount_info || ''} ${
        item.badge_text || ''
      }`.toLowerCase();

      if (selectedCategory === 'diskon') {
        return /diskon|hemat|paket|potongan|%|series|kopi|tea/i.test(text);
      }
      if (selectedCategory === 'ongkir') {
        return /ongkir|delivery|antar|kurir/i.test(text);
      }
      if (selectedCategory === 'cashback') {
        return /cashback|koin|poin|loyalty|bonus/i.test(text);
      }
      if (selectedCategory === 'member') {
        return /member|eksklusif|spesial|loyalty|vip/i.test(text);
      }
      return true;
    });

    return keywordMatches.length > 0 ? keywordMatches : allSortedPromos;
  }, [allSortedPromos, selectedCategory]);

  const handleOpenPromo = (promoId: string) => {
    onSelectPromo(promoId);
    try {
      window.history.pushState(null, '', `#promo?id=${encodeURIComponent(promoId)}`);
    } catch {
      window.location.hash = `#promo?id=${encodeURIComponent(promoId)}`;
    }
  };

  const handleVisualVoucherSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!voucherInput.trim()) {
      showToast('Silakan pilih promo dari daftar atau masukkan kode voucher resmi saat Checkout.', 'info');
      return;
    }
    showToast('Gunakan kode voucher resmi Anda langsung pada halaman Checkout saat memesan.', 'info');
  };

  const filterChips: { id: PromoFilterCategory; label: string }[] = [
    { id: 'all', label: 'Semua Promo' },
    { id: 'diskon', label: 'Kupon Diskon' },
    { id: 'ongkir', label: 'Gratis Ongkir' },
    { id: 'cashback', label: 'Cashback' },
    { id: 'member', label: 'Spesial Member' },
  ];

  return (
    <div className="font-jakarta min-h-full bg-[#faf8ff] text-[#131b2e] flex flex-col">
      {/* Top Header matching Google Stitch screen.png */}
      <header className="sticky top-0 w-full z-40 pt-[env(safe-area-inset-top,0px)] bg-white/95 backdrop-blur-xl shadow-[0_1px_8px_rgba(0,0,0,0.04)] shrink-0 border-b border-[#e2e7ff]/60">
        <div className="max-w-lg lg:max-w-3xl mx-auto h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              aria-label="Kembali"
              className="w-10 h-10 -ml-1.5 flex items-center justify-center rounded-full text-[#131b2e] hover:bg-[#f2f3ff] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5 stroke-[2.2]" />
            </button>
            <div className="flex flex-col">
              <h1 className="text-[18px] leading-[24px] font-bold tracking-[-0.01em] text-[#131b2e]">
                PROMO UNTUKMU
              </h1>
              <span className="text-[11px] leading-[14px] tracking-[0.04em] text-[#005136] font-semibold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#005136]" />
                <span>{activePromosCount} Promo Aktif Hari Ini</span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onNavigateTab('pesanan')}
              aria-label="Notifikasi"
              className="relative w-10 h-10 flex items-center justify-center rounded-full text-[#424654] hover:text-[#0040a1] transition-colors cursor-pointer"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-[#ba1a1a] ring-2 ring-white" />
            </button>
            <button
              type="button"
              onClick={() => onNavigateTab('akun')}
              aria-label="Profil Saya"
              className="w-10 h-10 flex items-center justify-center p-0.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-[#0040a1] flex items-center justify-center text-white">
                <UserRound className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Container */}
      <div className="flex-1 w-full max-w-lg lg:max-w-3xl mx-auto flex flex-col">
        {/* Category Filter Chips Bar */}
        <div className="sticky top-16 z-30 bg-[#faf8ff]/95 backdrop-blur-md pt-3 pb-2.5 px-4 border-b border-[#e2e7ff]/50">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5">
            {filterChips.map((chip) => {
              const isSelected = selectedCategory === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setSelectedCategory(chip.id)}
                  className={`px-4 py-2 rounded-full text-[12px] leading-[16px] font-semibold tracking-[0.02em] whitespace-nowrap active:scale-95 transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-[#0040a1] text-white shadow-xs'
                      : 'bg-white text-[#424654] hover:bg-[#f2f3ff] border border-[#c3c6d6]/40 shadow-2xs'
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Promo Digital Coupon Cards List */}
        <div className="flex flex-col gap-2.5 px-4 pt-3 pb-5">
          {filteredPromos.map((item, idx) => {
            const isActive = isSpecialPromoActive(item);
            const menuList = Array.isArray(data?.menuItems) ? data.menuItems : [];
            const linkedProduct =
              menuList.find((m) => m.id === item.product_id) ||
              menuList.find(
                (m) => m.name.trim().toLowerCase() === (item.title || '').trim().toLowerCase()
              );

            const theme = COUPON_THEMES[idx % COUPON_THEMES.length];
            const ThemeIcon = theme.Icon;
            const benefit = deriveRightPanelBenefit(item, linkedProduct);
            const content = deriveCouponContent(item, linkedProduct, idx);
            const bannerUrl = resolveMediaUrl(item.image_url);

            if (!isActive) {
              // Expired / Inactive Coupon Card matching screen.png
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenPromo(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenPromo(item.id);
                    }
                  }}
                  aria-label={`Promo berakhir: ${item.title}`}
                  className="relative rounded-[18px] overflow-hidden opacity-75 border border-slate-300 bg-linear-to-br from-[#64748B] to-[#94A3B8] text-white select-none cursor-pointer transition-opacity hover:opacity-85"
                >
                  <div className="relative z-10 flex flex-row items-stretch min-h-[125px]">
                    {/* Left Panel */}
                    <div className="flex-1 p-3.5 flex flex-col justify-between gap-1.5 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded-full bg-slate-900/30 text-[10px] font-bold tracking-wider uppercase text-white leading-none">
                          KEDALUWARSA
                        </span>
                      </div>

                      <div>
                        <div className="text-[18px] font-extrabold leading-tight tracking-tight text-white/85 line-through truncate">
                          {content.headline}
                        </div>
                        <h3 className="text-[13px] font-semibold text-white/85 leading-tight truncate mt-0.5">
                          {content.subtitle}
                        </h3>
                        <p className="text-[11px] text-white/75 line-clamp-1 leading-snug mt-0.5">
                          {content.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-0.5">
                        <div className="flex items-center gap-1 text-[11px] text-white/75 truncate">
                          <CalendarX2 className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{content.periodLabel}</span>
                        </div>
                        <button
                          type="button"
                          disabled
                          className="px-3 py-1 rounded-full bg-white/30 text-white/85 font-bold text-[11px] cursor-not-allowed inline-flex items-center gap-1 shrink-0"
                        >
                          <span>BERAKHIR</span>
                          <Lock className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Right Perforated Panel */}
                    <div className="w-24 sm:w-28 relative flex flex-col items-center justify-center p-2 text-center bg-black/10 border-l border-dashed border-white/15 shrink-0">
                      <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-1">
                        <CalendarX2 className="w-5 h-5 text-white/70" />
                      </div>
                      <span className="text-[13px] font-bold text-white/75 uppercase tracking-tight leading-none">
                        EXPIRED
                      </span>
                      {/* Circular Perforation Cutout */}
                      <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#faf8ff]" />
                    </div>
                  </div>
                </div>
              );
            }

            // Active Coupon Card matching screen.png
            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenPromo(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenPromo(item.id);
                  }
                }}
                aria-label={`Lihat detail promo: ${item.title}`}
                className={`relative rounded-[18px] overflow-hidden shadow-xs text-white ${theme.gradientClass} cursor-pointer select-none transition-transform active:scale-[0.99]`}
              >
                {/* Ambient Decorative Glow */}
                <div
                  className={`absolute rounded-full pointer-events-none ${theme.glowClass}`}
                />

                {/* Subtle CMS Banner Watermark Overlay */}
                {bannerUrl && !brokenBannerIds[item.id] && (
                  <img
                    src={bannerUrl}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover opacity-12 mix-blend-luminosity pointer-events-none"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => {
                      setBrokenBannerIds((prev) => ({ ...prev, [item.id]: true }));
                    }}
                  />
                )}

                <div className="relative z-10 flex flex-row items-stretch min-h-[125px]">
                  {/* Left Coupon Body */}
                  <div className="flex-1 p-3.5 flex flex-col justify-between gap-1.5 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase leading-none ${theme.badgeClass}`}
                      >
                        {content.badgeLabel}
                      </span>
                    </div>

                    <div>
                      <div className="text-[18px] font-extrabold leading-tight tracking-tight text-white truncate">
                        {content.headline}
                      </div>
                      <h3 className="text-[13px] font-semibold text-white/95 leading-tight truncate mt-0.5">
                        {content.subtitle}
                      </h3>
                      <p className="text-[11px] text-white/80 line-clamp-1 leading-snug mt-0.5">
                        {content.description}
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-0.5">
                      <div className="flex items-center gap-1 text-[11px] text-white/85 min-w-0 truncate">
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{content.periodLabel}</span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPromo(item.id);
                        }}
                        className={`px-3 py-1 rounded-full bg-white ${theme.buttonTextClass} font-bold text-[11px] shadow-xs hover:bg-white/90 active:scale-95 transition-all inline-flex items-center gap-1 shrink-0 cursor-pointer`}
                      >
                        <span>Lihat Promo</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Right Perforated Benefit Panel */}
                  <div className="w-24 sm:w-28 relative flex flex-col items-center justify-center p-2 text-center bg-white/10 backdrop-blur-[2px] border-l border-dashed border-white/25 shrink-0">
                    <div
                      className={`w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shadow-inner mb-1 border border-white/30 ${theme.iconRotateClass}`}
                    >
                      <ThemeIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[14px] sm:text-[15px] font-extrabold text-white tracking-tight leading-none">
                      {benefit.primary}
                    </span>
                    <span className="text-[9px] font-semibold text-white/85 uppercase tracking-widest mt-0.5 leading-none">
                      {benefit.secondary}
                    </span>

                    {/* Circular Perforation Cutout */}
                    <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#faf8ff]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Special Promo Code Visual Section ("Punya Kode Promo Khusus?") */}
        <div className="px-4 pb-8">
          <form
            onSubmit={handleVisualVoucherSubmit}
            className="p-4 rounded-2xl bg-white shadow-xs border border-[#e2e7ff]/80 flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#dae2ff] flex items-center justify-center text-[#0040a1] shrink-0">
                <Ticket className="w-5 h-5" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <p className="text-[12px] leading-[16px] text-[#131b2e] font-semibold">
                  Punya Kode Promo Khusus?
                </p>
                <p className="text-[12px] text-[#424654] truncate">
                  Ketik kode voucher dari mitra atau event spesial
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={voucherInput}
                onChange={(e) => setVoucherInput(e.target.value)}
                placeholder="Contoh: LETONKUHEMAT"
                className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl bg-[#f2f3ff] text-[#131b2e] text-[14px] leading-[20px] font-mono uppercase tracking-wider placeholder:normal-case placeholder:font-jakarta placeholder:text-[#424654]/60 focus:outline-none focus:ring-2 focus:ring-[#0040a1]/40"
              />
              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-[#0040a1] text-white font-bold text-[12px] leading-[16px] hover:bg-[#0056d2] active:scale-95 transition-all shadow-xs shrink-0 cursor-pointer"
              >
                Pakai
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
